import type { DocPage } from '../types'
import { media } from '../media'

export const valueMapping: DocPage = {
  id: 'value-mapping',
  title: 'Value mapping',
  description:
    'Build conditional value transforms with typed operators, Else modes (Use source value / Default value / Set as null), named references, and first-match rule order.',
  category: 'Mapping',
  sections: [
    { id: 'overview', title: 'Overview' },
    { id: 'scratch', title: 'Not for scratch' },
    { id: 'create', title: 'Create a value mapping' },
    { id: 'operators-string', title: 'Operators: string / text' },
    { id: 'operators-numeric', title: 'Operators: integer / decimal' },
    { id: 'operators-bool-enum', title: 'Operators: boolean & enum' },
    { id: 'operators-date', title: 'Operators: date / timestamp' },
    { id: 'else', title: 'Else (no match) modes' },
    { id: 'order', title: 'Rule order (first match)' },
    { id: 'assign', title: 'Assign reference' },
    { id: 'names', title: 'Name uniqueness' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'overview',
      text: 'Overview',
    },
    {
      type: 'paragraph',
      text: 'Value mapping transforms a source attribute into a target value using an ordered list of rules. Each rule has an Operator, a Compare Source Value, and a Resulting Target Value (or Set as null). When no rule matches, Else (no match) decides the outcome.',
    },
    {
      type: 'paragraph',
      text: 'You can keep value mappings as named references under Value Mappings and Assign this value mapping to one or more attribute mappings.',
    },
    {
      type: 'figure',
      src: media.addValueMappingModal,
      alt: 'Fixed mapping and value mapping dialogs',
      caption:
        'Figure: Add Value Mapping modal — Source/Target attribute, Name, Rules (Operator / Compare / Result), Else (no match), and Assign this value mapping.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'scratch',
      text: 'Not for scratch',
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'Scratch transfers',
      text: 'Value mapping is not available for scratch transfers. The V toolbar action is disabled. Use Fixed mapping (Set fixed value) for constants on scratch.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'create',
      text: 'Create a value mapping',
    },
    {
      type: 'steps',
      items: [
        'On a mapped attribute pair, click V (Add value mapping) or open Assign value mapping to pick an existing one.',
        'Enter a unique Name.',
        'Under Rules, choose Operator, Compare Source Value, and Resulting Target Value (or Set as null).',
        'Click Add rule for additional ordered rules.',
        'Configure Else (no match).',
        'Optionally enable Assign this value mapping when creating from an attribute context.',
        'Save — the mapping appears under Value Mappings for reuse.',
      ],
    },
    {
      type: 'table',
      headers: ['UI field', 'Purpose'],
      rows: [
        ['Name', 'Unique identifier for the value mapping reference.'],
        ['Rules', 'Ordered list of match → result rows.'],
        ['Operator', 'Comparison operator valid for the source type.'],
        ['Compare Source Value', 'Value(s) tested against the source attribute.'],
        ['Resulting Target Value', 'Value written when the rule matches.'],
        ['Set as null', 'Write null instead of a concrete result for that rule.'],
        ['Add rule', 'Append another rule below the existing ones.'],
        ['Else (no match)', 'Behavior when no rule matches.'],
        ['Assign this value mapping', 'Attach the definition to the current attribute mapping.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'operators-string',
      text: 'Operators: string / text',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Operator', 'Meaning'],
      rows: [
        ['Equals', 'Source equals the compare value.'],
        ['Not equals', 'Source differs from the compare value.'],
        ['CONTAINS', 'Source contains the compare substring.'],
        ['NOT_CONTAINS', 'Source does not contain the substring.'],
        ['STARTS_WITH', 'Source begins with the compare value.'],
        ['ENDS_WITH', 'Source ends with the compare value.'],
        ['MATCHES_REGEX', 'Source matches the regular expression.'],
        ['IN', 'Source is one of the listed values.'],
        ['NOT_IN', 'Source is not in the listed values.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'operators-numeric',
      text: 'Operators: integer / decimal',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Operator', 'Meaning'],
      rows: [
        ['EQ', 'Equal'],
        ['NE', 'Not equal'],
        ['LT', 'Less than'],
        ['LE', 'Less than or equal'],
        ['GT', 'Greater than'],
        ['GE', 'Greater than or equal'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'operators-bool-enum',
      text: 'Operators: boolean & enum',
    },
    {
      type: 'table',
      headers: ['Source type', 'Operators'],
      rows: [
        ['Boolean', 'EQ, NE'],
        ['Enum', 'Equals, Not equals'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'operators-date',
      text: 'Operators: date / timestamp',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Operator', 'Meaning'],
      rows: [
        ['Equals', 'Same date/timestamp.'],
        ['Not equals', 'Different date/timestamp.'],
        ['Before', 'Strictly earlier.'],
        ['On or before', 'Earlier or equal.'],
        ['After', 'Strictly later.'],
        ['On or after', 'Later or equal.'],
        ['In range', 'Within an inclusive/configured range in the UI.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'else',
      text: 'Else (no match) modes',
    },
    {
      type: 'table',
      headers: ['Select label', 'Internal mode', 'Behavior'],
      rows: [
        [
          'Use source value',
          'use_source',
          'Pass the original source value through (default when creating).',
        ],
        [
          'Default value',
          'default_value',
          'Write Default target value when nothing matches.',
        ],
        [
          'Set as null',
          'null (UI: set_null)',
          'Write null when nothing matches.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'order',
      text: 'Rule order (first match)',
    },
    {
      type: 'paragraph',
      text: 'Rules are evaluated top to bottom. The first matching rule wins; remaining rules are skipped. If none match, Else (no match) applies. Put more specific rules above broader ones (for example Equals before CONTAINS, or a specific IN list before a catch-all).',
    },
    {
      type: 'heading',
      level: 2,
      id: 'assign',
      text: 'Assign reference',
    },
    {
      type: 'steps',
      items: [
        'Open Assign value mapping on an attribute mapping.',
        'Read the prompt: Select a value mapping for this attribute mapping.',
        'Browse the Value Mappings folder / Available value mappings list.',
        'Select a compatible named mapping and click Assign.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'names',
      text: 'Name uniqueness',
    },
    {
      type: 'paragraph',
      text: 'Each value mapping Name must be unique in the workspace library. Reuse a name only by editing the existing definition, not by creating a second mapping with the same name. Clear names (for example status_active_to_1) make Assign lists easier to scan.',
    },
  ],
}
